import getConfig from 'next/config';

import { setLocal, getLocal, clearLocal } from 'utils/storage';

const { publicRuntimeConfig } = getConfig();

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

const normalizeEnvValue = (value) => {
  if (typeof value !== 'string') {
    return value || null;
  }

  const normalized = value.trim();
  const lowered = normalized.toLowerCase();

  if (!normalized || lowered === 'undefined' || lowered === 'null') {
    return null;
  }

  // Common placeholder values accidentally copied into .env/.env.local.
  if (
    lowered === 'changeme'
    || lowered === 'replace-me'
    || lowered === 'your_google_client_id'
    || normalized.includes('<')
    || normalized.includes('>')
  ) {
    return null;
  }

  return normalized;
};

const getBrowserOrigin = () => {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return null;
  }

  return window.location.origin;
};

const normalizeGraphqlHost = (value) => {
  const normalized = normalizeEnvValue(value);
  if (!normalized) {
    return null;
  }

  if (/localhost|127\.0\.0\.1|0\.0\.0\.0/i.test(normalized)) {
    const origin = getBrowserOrigin();
    if (!origin) {
      return null;
    }

    try {
      const url = new URL(normalized);
      return `${origin}${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      return null;
    }
  }

  return normalized;
};

const normalizeBrowserHost = (value) => {
  const normalized = normalizeEnvValue(value);
  if (!normalized) {
    return null;
  }

  if (/localhost|127\.0\.0\.1|0\.0\.0\.0/i.test(normalized)) {
    if (typeof window === 'undefined' || !window.location?.hostname) {
      return null;
    }

    try {
      const url = new URL(normalized);
      url.hostname = window.location.hostname;
      return url.toString();
    } catch (error) {
      return null;
    }
  }

  return normalized;
};

const GRAPHQL_PATH = () => {
  const runtimeHost = normalizeGraphqlHost(publicRuntimeConfig?.GRAPHQL_HOST);
  if (runtimeHost) {
    return runtimeHost;
  }

  const origin = getBrowserOrigin();
  return origin ? `${origin}/graphql` : '/graphql';
};
const SSE_PATH = () => {
  const runtimeHost = normalizeBrowserHost(publicRuntimeConfig?.SSE_HOST);
  if (runtimeHost) {
    return runtimeHost;
  }

  const graphqlHost = normalizeBrowserHost(publicRuntimeConfig?.GRAPHQL_HOST);
  if (graphqlHost) {
    return graphqlHost.replace('/graphql', '/sse');
  }

  return GRAPHQL_PATH()?.replace('/graphql', '/sse');
};

const normalizeTokenExpires = (expires) => {
  const fallback = Math.floor(Date.now() / 1000) + 60 * 60;

  if (!expires) {
    return fallback;
  }

  if (typeof expires === 'number') {
    return expires > 9999999999 ? Math.floor(expires / 1000) : expires;
  }

  const numericExpires = Number(expires);
  if (Number.isFinite(numericExpires)) {
    return numericExpires > 9999999999 ? Math.floor(numericExpires / 1000) : numericExpires;
  }

  const parsedExpires = Date.parse(expires);
  if (Number.isFinite(parsedExpires)) {
    return Math.floor(parsedExpires / 1000);
  }

  return fallback;
};

const setToken = (key, token, expires) => {
  if (!token) {
    return;
  }

  setLocal(key, JSON.stringify({
    value: token,
    expires: normalizeTokenExpires(expires),
  }));
};
const getToken = (key) => {
  const data = getLocal(key);
  if (!data) {
    return null;
  }

  const item = JSON.parse(data);
  if (Date.now() > item.expires * 1000) {
    clearLocal(key);
    return null;
  }

  return item.value;
};
const clearToken = (key) => clearLocal(key);

export {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,

  GRAPHQL_PATH,
  SSE_PATH,

  setToken,
  getToken,
  clearToken,
};
